import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from './Toast';
import './Hero.scss';

const SOCIALS = [
  {
    label: 'GitHub',
    href: 'https://github.com/fawaznp1',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/fawaznp1',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/fawaz_np/',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
      </svg>
    ),
  },
  {
    label: 'Twitter',
    href: 'https://twitter.com/fawaznp1',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

const Hero = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const roles = t('hero.roles', { returnObjects: true });

  useEffect(() => {
    const currentRole = roles[roleIndex];
    let timeout;
    if (!isDeleting && displayText === currentRole) {
      timeout = setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setRoleIndex((prev) => (prev + 1) % roles.length);
    } else if (isDeleting) {
      timeout = setTimeout(() => setDisplayText((prev) => prev.slice(0, -1)), 40);
    } else {
      timeout = setTimeout(
        () => setDisplayText(currentRole.slice(0, displayText.length + 1)),
        80 + Math.random() * 40
      );
    }
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, roleIndex]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let particles = [];
    let orbs = [];
    let width, height;
    const isDark = theme === 'dark';

    const resize = () => {
      width = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      height = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
    };
    resize();

    orbs = Array.from({ length: 5 }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: 120 + Math.random() * 200,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      hue: isDark ? 170 + Math.random() * 30 : 0 + Math.random() * 20,
      saturation: isDark ? 50 + Math.random() * 30 : 60 + Math.random() * 30,
      lightness: isDark ? 15 + Math.random() * 15 : 75 + Math.random() * 15,
    }));

    const GRID = 40;
    const buildParticles = (w, h) => {
      particles = [];
      for (let r = 0; r < Math.ceil(h / GRID) + 1; r++)
        for (let c = 0; c < Math.ceil(w / GRID) + 1; c++)
          particles.push({ baseX: c * GRID, baseY: r * GRID, x: c * GRID, y: r * GRID, size: 1.2 });
    };
    buildParticles(width, height);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      for (const orb of orbs) {
        orb.x += orb.vx; orb.y += orb.vy;
        if (orb.x < -orb.radius) orb.x = width + orb.radius;
        if (orb.x > width + orb.radius) orb.x = -orb.radius;
        if (orb.y < -orb.radius) orb.y = height + orb.radius;
        if (orb.y > height + orb.radius) orb.y = -orb.radius;
        const g = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        const a = isDark ? 0.12 : 0.15;
        g.addColorStop(0, `hsla(${orb.hue},${orb.saturation}%,${orb.lightness}%,${a})`);
        g.addColorStop(1, `hsla(${orb.hue},${orb.saturation}%,${orb.lightness}%,0)`);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2); ctx.fill();
      }
      const mx = mouseRef.current.x, my = mouseRef.current.y, IR = 140;
      for (const p of particles) {
        const dx = mx - p.baseX, dy = my - p.baseY, dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < IR) {
          const f = (1 - dist / IR) * 18;
          p.x = p.baseX - (dx / dist) * f; p.y = p.baseY - (dy / dist) * f;
          p.size = 1.2 + (1 - dist / IR) * 2.5;
        } else {
          p.x += (p.baseX - p.x) * 0.08; p.y += (p.baseY - p.y) * 0.08;
          p.size += (1.2 - p.size) * 0.08;
        }
        const prox = dist < IR ? 1 - dist / IR : 0;
        ctx.fillStyle = isDark
          ? `rgba(100,220,220,${0.08 + prox * 0.55})`
          : `rgba(126,5,5,${0.1 + prox * 0.45})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
      }
      if (mx > 0 && my > 0) {
        for (let i = 0; i < particles.length; i++) {
          const a = particles[i];
          const dA = Math.sqrt((mx - a.x) ** 2 + (my - a.y) ** 2);
          if (dA > IR) continue;
          for (let j = i + 1; j < particles.length; j++) {
            const b = particles[j];
            const dAB = Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
            if (dAB < 80) {
              const la = (1 - dAB / 80) * (1 - dA / IR) * (isDark ? 0.25 : 0.2);
              ctx.strokeStyle = isDark ? `rgba(100,220,220,${la})` : `rgba(126,5,5,${la})`;
              ctx.lineWidth = 0.6;
              ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
            }
          }
        }
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => { resize(); buildParticles(width, height); };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [theme]);

  useEffect(() => { const c = initCanvas(); return c; }, [initCanvas]);

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const handleMouseLeave = () => { mouseRef.current = { x: -1000, y: -1000 }; };

  const handleDownloadResume = () => {
    try {
      const link = document.createElement('a');
      link.href = '/Fawaz N P.pdf';
      link.download = 'Fawaz_NP_Resume.pdf';
      link.click();
      toast.success(t('hero.downloadSuccess'), { position: 'top-right' });
    } catch {
      toast.error(t('hero.downloadError'), { position: 'top-right' });
    }
  };

  return (
    <section
      className="hero"
      id="about"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <canvas ref={canvasRef} className="hero__canvas" />

      {/* floating code snippets */}
      <div className="hero__code-decor hero__code-decor--1" aria-hidden>
        <span className="hero__code-keyword">const</span> passion <span className="hero__code-op">=</span> <span className="hero__code-string">'infinite'</span>;
      </div>
      <div className="hero__code-decor hero__code-decor--2" aria-hidden>
        <span className="hero__code-keyword">while</span>(alive) {'{'} <span className="hero__code-fn">code</span>(); {'}'}
      </div>
      <div className="hero__code-decor hero__code-decor--3" aria-hidden>
        <span className="hero__code-comment">// turning ideas into reality</span>
      </div>

      <div className="hero__gradient" />

      {/* spinning ring — decorative, sits behind content */}
      <div className="hero__ring" aria-hidden>
        <div className="hero__ring-inner" />
      </div>

      <div className="hero__layout">
        <div className="hero__content">

          <div className="hero__badge">
            <span className="hero__badge-dot" />
            {t('hero.badge')}
          </div>

          <p className="hero__eyebrow">{t('hero.eyebrow')}</p>

          <h1 className="hero__title">
            Fawaz <span className="hero__title-accent">N P</span>
          </h1>

          <h2 className="hero__subtitle">
            <span className="hero__subtitle-static">{'> '}</span>
            <span className="hero__subtitle-typed">{displayText}</span>
            <span className="hero__cursor" />
          </h2>

          <p className="hero__description">{t('hero.description')}</p>

          <div className="hero__actions">
            <button className="hero__cta hero__cta--primary" onClick={handleDownloadResume}>
              <span className="hero__cta-content">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t('hero.downloadResume')}
              </span>
            </button>

            <button
              className="hero__cta hero__cta--outline"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="hero__cta-content">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {t('hero.getInTouch')}
              </span>
            </button>
          </div>

          {/* <div className="hero__social-row">
            <span className="hero__social-label">Find me on</span>
            <div className="hero__social-links">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero__social-icon"
                  aria-label={s.label}
                  title={s.label}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div> */}

        </div>
      </div>

      <div
        className="hero__scroll"
        onClick={() => document.querySelector('.about')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <div className="hero__scroll-mouse">
          <div className="hero__scroll-wheel" />
        </div>
        <span className="hero__scroll-text">{t('hero.scroll')}</span>
      </div>
    </section>
  );
};

export default Hero;
